import React from 'react';
import PropTypes from 'prop-types';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import constants from '../constants';

const {
  Stack,
  DialogHeader,
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  Box,
  Heading,
  Paragraph,
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionContent,
  ActionButton,
} = designSystem;

const {
  DEFAULT_OFFSET_X,
  DEFAULT_OFFSET_Y,
  DEFAULT_DIALOG_HEIGHT,
  DEFAULT_DIALOG_WIDTH,
  DEFAULT_DIALOG_MIN_HEIGHT,
} = constants;

const ButtonPresetSettingsDialog = ({
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  captions,
  settingsSections,
  onCancel,
  onSubmit,
  onOverlayClick,
  onManageColors,
  onDragEnd = Function.prototype,
}) => {
  const activeAccordionItems = settingsSections.map(({ id }) => id);
  return (
    <Modal
      draggable
      fullscreen={bowser.mobile}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      height={DEFAULT_DIALOG_HEIGHT}
      minHeight={DEFAULT_DIALOG_MIN_HEIGHT}
      offsetX={offsetX}
      offsetY={offsetY}
      className="ws-button-preset-settings-dialog"
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      overlay="none"
      resizeIcon="ns-resize"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onOverlayClick={onOverlayClick}
      onDragEnd={onDragEnd}
    >
      <PanelGroup height="100%" className="ws-button-preset-settings-dialog__panel-group">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger ws-button-preset-settings-dialog__header"
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3">{captions.title}</Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel
          height="100%"
          scrollable
          className="ws-button-preset-settings-dialog__content-wrapper"
        >
          <div className="ws-button-preset-settings-dialog__content">
            <Accordion appearance="grey" defaultActiveItem={activeAccordionItems}>
              {settingsSections.map(({ id: sectionId, title: sectionTitle }) => (
                <AccordionGroup key={sectionId} itemId={sectionId}>
                  <AccordionHeader title={sectionTitle.toUpperCase()} isSticky />
                  <AccordionContent>
                    <Box padding="spacing-xs" paddingLeft="spacing-m" paddingRight="spacing-m">
                      <div className="ws-button-preset-settings-dialog__setting">
                        <Heading type="heading-6">{captions.settingTitle}</Heading>
                        <ActionButton
                          format="text"
                          iconGlyph="small-right-arrow"
                          iconPlacement="after"
                          label={captions.settingLabel}
                          onClick={() => onManageColors(sectionId, sectionTitle)}
                        />
                      </div>
                    </Box>
                  </AccordionContent>
                </AccordionGroup>
              ))}
            </Accordion>
          </div>
        </Panel>
        <Panel corners="squared" className="ws-button-preset-settings-dialog__footer">
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

ButtonPresetSettingsDialog.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    settingTitle: PropTypes.string.isRequired,
    settingLabel: PropTypes.string.isRequired,
    submitButton: PropTypes.string.isRequired,
    cancelButton: PropTypes.string.isRequired,
  }).isRequired,
  settingsSections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOverlayClick: PropTypes.func.isRequired,
  onManageColors: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func,
};

export default ButtonPresetSettingsDialog;
